import { useContext, useState, useEffect, useRef } from 'react';
import {
  Avatar,
  Box,
  Divider,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { AllInbox, Delete, QrCode } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { CustomPagination, findOnArray } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { EstoqueContext } from 'contexts/EstoqueContext';
import { ProdutosContext } from 'contexts/ProdutosContext';
import Card from 'components/Card';
import Input from 'components/Input';
import Button from 'components/Button';
import Header from 'components/Header';
import Dropdown from 'components/Dropdown';
import InputMask from 'components/InputMask';
import Container from 'components/Container';
import VirtualDrop from 'components/VirtualDrop';
import InfosProduto from 'components/InfosProduto';
import Blank from 'assets/blank.png';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const DECIMAL_SCALE = 6;
const DECIMAL_FACTOR = 10 ** DECIMAL_SCALE;

const toScaledInteger = (value) =>
  Math.round((Number(value) || 0) * DECIMAL_FACTOR);

const fromScaledInteger = (value) =>
  Number((value / DECIMAL_FACTOR).toFixed(DECIMAL_SCALE));

const normalizeDecimal = (value) => fromScaledInteger(toScaledInteger(value));

const subtractDecimals = (minuend, subtrahend) =>
  fromScaledInteger(toScaledInteger(minuend) - toScaledInteger(subtrahend));

const Inventário = () => {
  let timer;
  const waitTime = 1000;
  const { user } = useContext(AuthContext);
  const defaultValues = {
    filial_id: user?.filial_id,
    celocal_id: null,
    dtemissao: moment().format('YYYY-MM-DD HH:mm:ss'),
    //ITENS
    produto_id: null,
    cdbarra: '',
    quantidade: '',
    validade: null,
    variacao_id: null,
    rastreabilidade: '',
    itens: [],
  };
  const produtoRef = useRef(null);
  const cdbarraRef = useRef(null);
  const [produto, setProduto] = useState(null);
  const [useBarCode, setUserBarCode] = useState(false);
  const { drops } = useContext(DropsContext);
  const { getProduto, getLoading } = useContext(ProdutosContext);
  const { postLoading, postMovimentacoes } = useContext(EstoqueContext);
  const { control, handleSubmit, setValue, watch } = useForm({ defaultValues });
  const itens = watch('itens');
  const filial_id = watch('filial_id');
  const dtemissao = watch('dtemissao');
  const produto_id = watch('produto_id');
  const celocal_id = watch('celocal_id');
  const quantidade = watch('quantidade');
  const dropLocalEstoque = drops?.LocalEstoque?.filter(
    (f) => !Boolean(f?.filial_id) || f?.filial_id === filial_id
  );
  const dropVariacao = drops?.ProdutoVariacao?.filter(
    (f) => Boolean(f?.produto_id) === produto_id
  );

  useEffect(() => {
    if (produto_id) {
      const tempo_saldo =
        dtemissao && moment(dtemissao).format('YYYY-MM-DD HH:mm:ss');
      const cb = (res) => {
        const descricao = findOnArray(res?.id, drops?.Produto, 'label');
        const imagem = res?.ProdutoImagems?.find(
          (f) => f?.Midium?.tipo === 'IMAGEM'
        )?.Midium?.url;
        const newProd = { ...res, imagem, descricao };

        setProduto(newProd);

        if (!Boolean(quantidade)) {
          setValue('quantidade', newProd?.volume || 1);
        }
      };

      getProduto({
        cb,
        filial_id,
        tempo_saldo,
        id: produto_id,
        tipo_saldo: 'FISICO',
        local_id: celocal_id,
        saldo_detalhado: true,
      });
    } else {
      setValue('cdbarra', '');
      setValue('quantidade', '');
      setProduto(null);
    }
    setValue('validade', null);
    setValue('variacao_id', null);
    setValue('rastreabilidade', '');
  }, [produto_id]);

  useEffect(() => {
    setValue('produto_id', null);
  }, [useBarCode]);

  const handleRead = (e) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const value = e?.target?.value;
      if (Boolean(value)) {
        const produto = drops?.Produto.find(
          (f) => f?.cdbarra == e?.target?.value
        );
        setValue('produto_id', produto?.value || null);
      }
    }, waitTime);
  };

  const onAddItem = (values) => {
    if (!Boolean(values?.produto_id)) {
      return toastWarning('Nenhum produto selecionado');
    }

    const quantidade = normalizeDecimal(values?.quantidade);

    if (
      values?.quantidade === '' ||
      values?.quantidade === null ||
      values?.quantidade === undefined ||
      !Number.isFinite(quantidade) ||
      quantidade < 0
    ) {
      return toastWarning('Quantidade inválida');
    }

    if (produto?.variacao === 'SIM' && !values?.variacao) {
      return toastWarning('Variação não informada');
    }

    if (produto?.rastreavel === 'SIM' && !values?.rastreabilidade) {
      return toastWarning('Rastreabilidade não informada');
    }

    const uuid = `${values?.produto_id || '_'}${values?.validade || '_'}${values?.variacao_id || '_'}${values?.rastreabilidade || '_'}`;

    const produto_duplicado = values?.itens.some((s) => s?.uuid === uuid);

    if (produto_duplicado) {
      return toastWarning('Produto já inserido');
    }

    let saldo = normalizeDecimal(produto?.saldo);

    if (
      produto?.saldo_detalhado?.some(
        (s) =>
          Boolean(s?.rastreabilidade) ||
          Boolean(s?.variacao_id) ||
          Boolean(s?.validade)
      )
    ) {
      saldo =
        normalizeDecimal(
          produto?.saldo_detalhado?.find(
            (f) =>
              f?.rastreabilidade === values?.rastreabilidade &&
              f?.variacao_id === values?.variacao_id &&
              f?.validade === values?.validade
          )?.saldo
        ) || 0;
    }

    const ajuste = subtractDecimals(quantidade, saldo);

    const newItem = {
      uuid,
      saldo,
      ajuste,
      produto_id: values?.produto_id,
      quantidade,
      validade: values?.validade,
      variacao_id: values?.variacao_id,
      rastreabilidade: values?.rastreabilidade,
      imagem: produto?.imagem,
      descricao: produto?.descricao,
      referencia: produto?.referencia,
      localizador: produto?.localizador,
      unidade_medida: produto?.UnidadeMedida?.sigla,
    };

    setValue('itens', [newItem, ...values?.itens]);
    setValue('produto_id', null);

    if (produtoRef.current) {
      produtoRef.current.focus();
    }
    if (cdbarraRef.current) {
      cdbarraRef.current.focus();
    }
  };

  const onDelete = (row) => {
    setValue(
      'itens',
      itens?.filter((i) => i?.uuid !== row?.uuid)
    );
  };

  const onSubmit = (values) => {
    const Entradas = values?.itens
      ?.filter((f) => f?.ajuste > 0)
      ?.map((i, index) => ({
        sequencia: index + 1,
        produto_id: i?.produto_id,
        descricao: i?.descricao,
        quantidade: Math.abs(i?.ajuste),
        validade: i?.validade,
        produto_variacao_id: i?.variacao_id,
        rastreabilidade: i?.rastreabilidade,
        dhsaldo: values?.dtemissao,
      }));

    const Saidas = values?.itens
      ?.filter((f) => f?.ajuste < 0)
      ?.map((i, index) => ({
        sequencia: index + 1,
        produto_id: i?.produto_id,
        descricao: i?.descricao,
        referencia: i?.referencia,
        quantidade: Math.abs(i?.ajuste),
        validade: i?.validade,
        produto_variacao_id: i?.variacao_id,
        rastreabilidade: i?.rastreabilidade,
        dhsaldo: values?.dtemissao,
      }));

    const data = {
      inventario: true,
      Documento: {
        users_id: user?.id,
        filial_id: values?.filial_id,
        cadastro_id: values?.filial_id,
        celocal_id: values?.celocal_id,
        dtemissao: values?.dtemissao,
      },
      ...(Entradas?.length > 0 && { Entradas }),
      ...(Saidas?.length > 0 && { Saidas }),
    };

    const cb = () => {
      setValue('filial_id', user?.filial_id);
      setValue('celocal_id', null);
      setValue('dtemissao', moment().format('YYYY-MM-DD HH:mm:ss'));
      setValue('produto_id', null);
      setValue('cdbarra', '');
      setValue('quantidade', '');
      setValue('validade', null);
      setValue('variacao_id', null);
      setValue('rastreabilidade', '');
      setValue('itens', []);
    };

    if (Entradas?.length > 0 || Saidas?.length > 0) {
      postMovimentacoes({ data, cb });
    } else {
      toastWarning('Nenhum ajuste necessário a ser efetuado');
    }
  };

  const SaldoCell = ({ row }) => {
    const lines = [
      {
        label: 'Contagem',
        value: `${row?.quantidade || 0} ${row?.unidade_medida || ''}`,
      },
      {
        label: 'Atual',
        value: `${row?.saldo || 0} ${row?.unidade_medida || ''}`,
      },
      {
        label: 'Ajuste',
        value: `${row?.ajuste || 0} ${row?.unidade_medida || ''}`,
        divider: true,
      },
    ];

    return (
      <Box flex={1} justifyContent="space-between">
        {lines?.map(({ label, value, divider }) => (
          <>
            {divider && <Divider />}
            <Box display="flex" alignItems="center" flex={1}>
              <Typography variant="caption" fontWeight="bold" flex={3}>
                {label}:{' '}
              </Typography>
              <Typography variant="caption">{value}</Typography>
            </Box>
          </>
        ))}
      </Box>
    );
  };

  const ProdutoCell = ({ row }) => {
    const lines = [
      { label: 'Referência', value: row?.referencia },
      { label: 'Localizador', value: row?.localizador },
      {
        label: 'Validade',
        value: row?.validade && moment(row?.validade).format('DD/MM/YYYY'),
      },
      { label: 'Rastreabilidade', value: row?.rastreabilidade },
      {
        label: 'Variação',
        value: findOnArray(row?.variacao_id, drops?.ProdutoVariacao, 'label'),
      },
    ];

    return (
      <Box py={1} gap={2} flex={1} display="flex" alignItems="center">
        <Avatar
          variant="rounded"
          sx={styles?.imageGrid}
          src={row?.imagem || Blank}
        />
        <Box flex={1} display="flex" flexDirection="column">
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                {row?.descricao}
              </Typography>
            </Grid>
            {lines.map(({ label, value }) => (
              <Grid item xs={6} key={label}>
                <Box
                  gap={1}
                  flex={1}
                  display="flex"
                  alignItems="center"
                  color="text.secondary"
                >
                  <Typography variant="caption" fontWeight="bold" flex={1}>
                    {label}:
                  </Typography>
                  <Typography variant="caption" flex={2}>
                    {value || '-'}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <Container>
      <Header titulo="Inventário" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sx={styles?.gridInfo}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card title="Informações Gerais">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Dropdown
                      name="filial_id"
                      control={control}
                      label="Filial"
                      options={drops?.Filial}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Dropdown
                      name="celocal_id"
                      control={control}
                      label="Local de Estoque"
                      options={dropLocalEstoque}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputMask
                      type="datetime"
                      name="dtemissao"
                      control={control}
                      label="Data da Contagem"
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card title="Produto">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Tabs
                      sx={styles?.tab}
                      variant="fullWidth"
                      value={useBarCode ? 1 : 0}
                      onChange={(_, v) => setUserBarCode(Boolean(v))}
                    >
                      <Tab
                        value={0}
                        icon={<AllInbox sx={styles?.tabIcon} />}
                        iconPosition="start"
                        label="Digitação"
                      />
                      <Tab
                        value={1}
                        icon={<QrCode sx={styles?.tabIcon} />}
                        iconPosition="start"
                        label="Leitura"
                      />
                    </Tabs>
                  </Grid>
                  <Grid item xs={12} display={useBarCode ? 'block' : 'none'}>
                    <Input
                      name="cdbarra"
                      control={control}
                      onKeyUp={handleRead}
                      inputRef={cdbarraRef}
                      label="Código de Barras"
                    />
                  </Grid>
                  <Grid item xs={12} display={useBarCode ? 'none' : 'block'}>
                    <VirtualDrop
                      busca_refinada
                      label="Produto"
                      name="produto_id"
                      refresh="Produto"
                      control={control}
                      inputRef={produtoRef}
                      options={drops?.Produto}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputMask
                      name="quantidade"
                      control={control}
                      label="Quantidade"
                      decimalScale={DECIMAL_SCALE}
                      allowNegative={false}
                      allowedDecimalSeparators={[',', '.']}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputMask
                      type="date"
                      name="validade"
                      label="Validade"
                      control={control}
                    />
                  </Grid>
                  {produto?.rastreavel === 'SIM' && (
                    <Grid item xs={12} md={6}>
                      <Input
                        control={control}
                        name="rastreabilidade"
                        label="Rastreabilidade"
                      />
                    </Grid>
                  )}
                  {produto?.variacao === 'SIM' && (
                    <Grid item xs={12} md={6}>
                      <Dropdown
                        label="Variação"
                        control={control}
                        name="variacao_id"
                        options={dropVariacao}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <InfosProduto produto={produto} loading={getLoading} />
                  </Grid>
                  <Grid item xs={12} textAlign="center">
                    <Button
                      color="primary"
                      variant="outlined"
                      loading={getLoading}
                      onClick={handleSubmit(onAddItem)}
                    >
                      ADICIONAR
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={8} display="flex" p={1}>
          <Card title="Itens" style={styles?.card}>
            <DataGrid
              rows={itens}
              columns={[
                {
                  field: 'produto',
                  headerName: 'Produto',
                  minWidth: 400,
                  flex: 1,
                  sortable: false,
                  renderCell: (props) => <ProdutoCell {...props} />,
                },
                {
                  field: 'saldo',
                  headerName: 'Saldo',
                  width: 200,
                  sortable: false,
                  renderCell: (props) => <SaldoCell {...props} />,
                },
                {
                  field: 'actions',
                  type: 'actions',
                  headerName: 'Ações',
                  width: 100,
                  getActions: ({ row }) => {
                    return [
                      <GridActionsCellItem
                        icon={<Delete />}
                        label="Excluir"
                        onClick={() => onDelete(row)}
                      />,
                    ];
                  },
                },
              ]}
              pagination
              density="compact"
              disableColumnMenu
              showCellRightBorder
              showColumnRightBorder
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              getRowId={(row) => row.uuid}
              localeText={localeText}
              loading={getLoading}
              components={{
                LoadingOverlay: LinearProgress,
                Pagination: CustomPagination,
              }}
            />
          </Card>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={postLoading}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Inventário;
